// =============================================================================
// app/src/lib/lerDasArquivo.ts — a metade do lerDas que precisa do navegador.
//
// O par puro/DOM é o mesmo de lerBoleto.ts e lerBoletoArquivo.ts: a conta fica
// no arquivo testável e só o que depende de API do ambiente mora aqui.
//
// SEM DEPENDÊNCIA NOVA. `DecompressionStream` é nativo no Safari 16.4+ (o
// iPhone do dono), no Chrome 80+ e no Node — o que deixa isto testável no
// vitest com um PDF montado por `zlib.deflateSync`. Trazer pdf.js custaria
// ~1 MB num PWA que o dono abre no 4G, para ler uma linha que já está escrita
// em letra ao lado do código de barras.
//
// TUDO NO APARELHO. O PDF não sai do celular, como já é hoje no leitor de foto.
// =============================================================================
import { dicionarioAntes, indiceDe, latin1 } from './lerBoleto';
import { camposDoDas, sopaDeConteudo } from './lerDas';
import type { CamposDoDas } from './lerDas';

/** Teto por arquivo: iPhone velho não pode travar num PDF gigante ou torto. */
const MAX_STREAMS = 40;
const MAX_BYTES_POR_STREAM = 4 * 1024 * 1024;

/**
 * Os streams de CONTEÚDO — a varredura do `fotosDoPdfEmOrdem`, com o filtro do
 * dicionário invertido: quem TEM /FlateDecode em vez de quem tem /DCTDecode.
 *
 * Fora, de propósito:
 *   * `/Subtype /Image` — foto comprimida com Flate é caminho do outro leitor;
 *   * `/Predictor` — stream de xref infla para bytes com predição PNG, que é
 *     ruído puro e só faria a sopa engordar.
 */
export function streamsComprimidos(pdf: Uint8Array): Uint8Array[] {
  const achados: Uint8Array[] = [];
  let desde = 0;
  while (achados.length < MAX_STREAMS) {
    const st = indiceDe(pdf, 'stream', desde);
    if (st < 0) break;
    if (latin1(pdf, Math.max(0, st - 3), st) === 'end') { desde = st + 6; continue; }
    let ini = st + 6;
    if (pdf[ini] === 0x0d) ini += 1;
    if (pdf[ini] !== 0x0a && pdf[ini - 1] !== 0x0d) { desde = st + 6; continue; }
    if (pdf[ini] === 0x0a) ini += 1;
    const dic = dicionarioAntes(pdf, st);
    if (dic === null) { desde = st + 6; continue; }
    const fimStream = indiceDe(pdf, 'endstream', ini);
    if (fimStream < 0) break;
    if (/\/FlateDecode/.test(dic) && !/\/Subtype\s*\/Image/.test(dic) && !/\/Predictor/.test(dic)) {
      let fim = fimStream;
      while (fim > ini && (pdf[fim - 1] === 0x0a || pdf[fim - 1] === 0x0d)) fim -= 1;
      if (fim - ini > 0 && fim - ini <= MAX_BYTES_POR_STREAM) achados.push(pdf.subarray(ini, fim));
    }
    desde = fimStream + 9;
  }
  return achados;
}

/**
 * Inflar, com recuo obrigatório.
 *
 * Um stream deflate CRU pedido como zlib é recusado com TypeError — medido. O
 * recuo para 'deflate-raw' não é enfeite: sem ele, gerador que escreve sem o
 * cabeçalho zlib devolve zero texto e o DAS inteiro vira silêncio.
 */
async function inflar(bytes: Uint8Array): Promise<string | null> {
  for (const formato of ['deflate', 'deflate-raw'] as const) {
    try {
      const ds = new DecompressionStream(formato);
      const escritor = ds.writable.getWriter();
      // slice(): o subarray do PDF carrega ArrayBufferLike, e o
      // DecompressionStream quer BufferSource. Copia curta, uma vez por stream.
      void escritor.write(bytes.slice());
      void escritor.close();
      const saida = await new Response(ds.readable).arrayBuffer();
      const b = new Uint8Array(saida);
      if (b.length > 0) return latin1(b, 0, b.length);
    } catch {
      // formato errado: tenta o próximo, e se os dois falharem some em silêncio
    }
  }
  return null;
}

/**
 * O texto que o PDF escreve (a "sopa" de lerDas.ts), ou vazio se ele não tem
 * texto legível — digitalização, PDF cifrado, fonte com codificação própria.
 * O leitor de boleto (lerBoletoArquivo.ts) usa também: o PDF que o banco
 * manda por e-mail traz a linha digitável escrita.
 */
export async function textoDoPdf(pdf: Uint8Array): Promise<string> {
  // PDF cifrado infla para lixo: não vale gastar o processador do celular.
  if (indiceDe(pdf, '/Encrypt', 0) >= 0) return '';
  let sopa = '';
  for (const s of streamsComprimidos(pdf)) {
    const texto = await inflar(s);
    if (texto === null) continue;
    sopa += (sopa ? '\n' : '') + sopaDeConteudo(texto);
  }
  return sopa;
}

export type LeituraDoDas =
  | { ok: true; campos: CamposDoDas }
  | { ok: false; motivo: string };

/**
 * O DAS de um PDF, ou a razão de não ter dado.
 *
 * `motivo` vazio quer dizer "este PDF não é texto legível" — e aí quem chama
 * deve mostrar a mensagem que já mostrava. Motivo com texto é recado para o
 * dono (dois documentos no mesmo arquivo, por exemplo).
 */
export async function lerDasDoPdf(pdf: Uint8Array, hojeIso: string): Promise<LeituraDoDas> {
  const sopa = await textoDoPdf(pdf);
  if (!sopa.trim()) return { ok: false, motivo: '' };

  const r = camposDoDas(sopa, hojeIso);
  if ('erro' in r) return { ok: false, motivo: r.erro === 'sem-linha' ? '' : r.erro };
  return { ok: true, campos: r };
}
